import type { FirmwareReleasePackage } from 'api/admin/firmware/firmware';
import httpClient from '../../httpClient';

export enum BulkDeviceAction {
  Reboot = 'reboot',
  Reinitialize = 'reinitialize',
  DownloadLogs = 'downloadLogs',
}

export enum BulkDeviceActionLabel {
  Reboot = 'Reboot',
  Reinitialize = 'Reinitialize',
  DownloadLogs = 'Download logs',
}

export enum BulkDeviceActionErrorMessage {
  Reboot = 'Failed to reboot',
  Reinitialize = 'Failed to reinitialize device',
  DownloadLogs = 'Failed to trigger log download',
}

export type WashboardDevice = {
  id: number;
  name: string;
  deviceType: DeviceType;
  status: DeviceStatus;
  systemStatus: SystemStatus;
  ipAddress: string;
  communicationProtocol: string;
  machineName: string;
  machineType: MachineType;
  businessName: string;
  businessId: number;
  storeName: string;
  storeId: number;
  releaseId: number | null;
  releaseVersion: string | null;
};

export enum DeviceType {
  PENNY = 'PENNY',
  PULSE = 'PULSE',
  DIME = 'DIME',
  LAUNDROWORKS = 'LAUNDROWORKS',
}

export enum DeviceStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  IN_USE = 'IN_USE',
  PENDING_START = 'PENDING_START',
  COMPLETED = 'COMPLETED',
}

export enum SystemStatus {
  GOOD = 'GOOD',
  RESTARTED = 'RESTARTED',
  STOPPED = 'STOPPED',
  UNKNOWN = 'UNKNOWN',
}

export enum MachineType {
  WASHER = 'WASHER',
  DRYER = 'DRYER',
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export const WASHBOARD_SORTABLE_FIELDS = [
  'name',
  'deviceType',
  'status',
  'systemStatus',
  'ipAddress',
  'machineType',
  'businessName',
  'storeName',
] as const;

export type WashboardSortableField = (typeof WASHBOARD_SORTABLE_FIELDS)[number];

export type GetWashboardDevicesResponse = {
  success: boolean;
  devices: WashboardDevice[];
  hasMore: boolean;
  total: number;
};

export type GetWashboardDevicesParams = {
  page: number;
  limit: number;
  sortBy?: WashboardSortableField;
  sortOrder?: SortOrder;
  businessId?: number;
  storeId?: number;
  deviceType?: DeviceType;
  searchTerm?: string;
};

export type UpdateLocationEnvVarsPayload = {
  envVariables: Record<string, string>;
};

export type UpdateLocationEnvVarsResponse = {
  success: boolean;
};

export enum DeviceReleaseUpdateStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  TIMED_OUT = 'TIMED_OUT',
  CANCELLED = 'CANCELLED',
}

const ACTIVE_RELEASE_UPDATE_STATUSES = [
  DeviceReleaseUpdateStatus.PENDING,
  DeviceReleaseUpdateStatus.IN_PROGRESS,
];

export const isReleaseUpdateActive = (status?: DeviceReleaseUpdateStatus | null): boolean =>
  Boolean(status && ACTIVE_RELEASE_UPDATE_STATUSES.includes(status));

export enum DeviceReleaseUpdateEventState {
  IDLE = 'IDLE',
  DOWNLOAD = 'DOWNLOAD',
  INSTALL = 'INSTALL',
  REBOOT_DELAY = 'REBOOT_DELAY',
  REBOOT = 'REBOOT',
  VERIFY = 'VERIFY',
  ROLLBACK = 'ROLLBACK',
  ERROR = 'ERROR',
}

export type DeviceReleaseUpdateProgress = {
  status: DeviceReleaseUpdateStatus;
  targetReleaseVersion: string;
  packages: FirmwareReleasePackage[];
  state: DeviceReleaseUpdateEventState | null;
  downloadProgress: string | null;
  installProgress: string | null;
  errorText: string | null;
  updatedAt: string;
};

export type WashboardDeviceDetails = {
  deviceName: string;
  systemStatus: string;
  machineName: string | null;
  machineType: MachineType | null;
  machineUpperName: string | null;
  machineUpperStatus: string | null;
  machineStatus: string | null;
  deviceType: DeviceType | null;
  locationId: number;
  locationName: string;
  businessId: number;
  businessName: string;
  environmentVariables: Record<string, string> | null;
  pendingEnvVariables: Record<string, string> | null;
  communicationProtocol: string | null;
  releaseId: number | null;
  releaseVersion: string | null;
  packages: FirmwareReleasePackage[];
  releaseUpdate: DeviceReleaseUpdateProgress | null;
};

export type GetWashboardDeviceDetailsResponse = {
  success: boolean;
  device: WashboardDeviceDetails;
};

export type UpdateWashboardDevicePayload = {
  machineName?: string | null;
  upperMachineName?: string | null;
  locationId?: number;
  envVariables?: Record<string, string>;
  releaseId?: number;
};

export type UpdateWashboardDeviceResponse = {
  success: boolean;
};

export type UpdateWashboardDevicesPayload = {
  deviceIds: number[];
  locationId?: number;
  envVariables?: Record<string, string>;
  releaseId?: number;
};

export type UpdateWashboardDevicesResponse = {
  success: boolean;
};

export type DeviceEnvVariablesForDevice = {
  deviceId: number;
  envVariables: Record<string, string>;
  updatedAt: string;
};

export type GetEnvVariablesForDevicesPayload = {
  deviceIds: number[];
};

export type GetEnvVariablesForDevicesResponse = {
  success: boolean;
  devices: DeviceEnvVariablesForDevice[];
};

export enum DeviceActivityLogType {
  FIRMWARE_UPDATE = 'FIRMWARE_UPDATE',
  ENV_VARS_UPDATE = 'ENV_VARS_UPDATE',
  LOCATION_UPDATE = 'LOCATION_UPDATE',
  REBOOT = 'REBOOT',
}

export type DeviceActivityLog = {
  id: number;
  type: DeviceActivityLogType;
  text: string | null;
  createdAt: string;
};

export type GetDeviceActivityLogsParams = {
  page: number;
  limit: number;
};

export type GetDeviceActivityLogsResponse = {
  success: boolean;
  activityLogs: DeviceActivityLog[];
  hasMore: boolean;
  total: number;
};

export const getWashboardDevices = (params: GetWashboardDevicesParams) => {
  return httpClient({
    method: 'GET',
    url: 'super-admin/washboard/devices',
    params,
  });
};

export const getWashboardDeviceDetails = (deviceId: number) => {
  return httpClient({
    method: 'GET',
    url: `super-admin/washboard/devices/${deviceId}`,
  });
};

export const updateWashboardDevice = (deviceId: number, payload: UpdateWashboardDevicePayload) => {
  return httpClient({
    method: 'PATCH',
    url: `super-admin/washboard/devices/${deviceId}`,
    data: payload,
  });
};

export const updateWashboardDevices = (payload: UpdateWashboardDevicesPayload) => {
  return httpClient({
    method: 'PATCH',
    url: 'super-admin/washboard/devices',
    data: payload,
  });
};

export const getEnvVariablesForDevices = (payload: GetEnvVariablesForDevicesPayload) => {
  return httpClient({
    method: 'POST',
    url: 'super-admin/washboard/devices/envVariables',
    data: payload,
  });
};

export const rebootWashboardDevice = (deviceId: number) => {
  return httpClient({
    method: 'POST',
    url: `super-admin/washboard/devices/${deviceId}/reboot`,
  });
};

export const downloadLogsWashboardDevice = (deviceId: number) => {
  return httpClient({
    method: 'POST',
    url: `super-admin/washboard/devices/${deviceId}/download-logs`,
  });
};

export type DeviceScreenCaptureResponse = {
  success: boolean;
  url: string | null;
  lastRefreshedAt: string | null;
};

export const getDeviceScreenCapture = (deviceId: number) => {
  return httpClient({
    method: 'GET',
    url: `super-admin/washboard/devices/${deviceId}/screen-capture`,
  });
};

export const getDeviceActivityLogs = (deviceId: number, params: GetDeviceActivityLogsParams) => {
  return httpClient({
    method: 'GET',
    url: `super-admin/washboard/devices/${deviceId}/activity-logs`,
    params,
  });
};

export const updateLocationEnvVars = (
  locationId: number,
  payload: UpdateLocationEnvVarsPayload,
) => {
  return httpClient({
    method: 'PATCH',
    url: `super-admin/washboard/locations/${locationId}/env-vars`,
    data: payload,
  });
};
